import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  BookOpen,
  Layers,
  PlayCircle,
  Users,
  Calendar,
  TrendingUp,
  ArrowRight,
  Download,
  Mail,
  Brain,
  MessageCircle,
  Sparkles,
  BarChart3,
  RefreshCw,
  Loader2,
  Hotel,
  Megaphone,
  Briefcase,
  Inbox,
  Leaf,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API = `${window.location.origin}/api`;
const CHART_COLORS = ["#8b5cf6", "#ec4899", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalBookings: 0,
    totalUsers: 0,
    upcomingCourses: 0,
  });
  const [wellnessStats, setWellnessStats] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchWellnessStats();
    fetchSyncStatus();
    fetchAiStats();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSync(response.data);
    } catch (error) {
      console.log("Sync status not available");
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/sync-from-live`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sincronizzazione avviata! I dati verranno aggiornati in pochi secondi.");
      // Ricarica i dati dopo 5 secondi
      setTimeout(() => {
        fetchStats();
        fetchSyncStatus();
        setSyncing(false);
      }, 5000);
    } catch (error) {
      toast.error("Errore durante la sincronizzazione");
      setSyncing(false);
    }
  };

  const fetchWellnessStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/wellness-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWellnessStats(response.data);
    } catch (error) {
      console.error("Errore caricamento wellness stats:", error);
    }
  };

  const fetchAiStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiStats(response.data);
    } catch (error) {
      console.error("Errore caricamento AI stats:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const [sessionsRes, bookingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/sessions`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/users`),
      ]);

      const today = new Date();
      const upcomingSessions = sessionsRes.data.filter(
        (s) => new Date(s.date) >= today
      ).length;

      setStats({
        totalCourses: sessionsRes.data.length,
        totalBookings: bookingsRes.data.filter((b) => b.status === "confirmed").length,
        totalUsers: usersRes.data.length,
        upcomingCourses: upcomingSessions,
      });

      setRecentBookings(
        bookingsRes.data
          .filter((b) => b.status === "confirmed")
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5)
      );
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Corsi Totali",
      value: stats.totalCourses,
      icon: BookOpen,
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      title: "Iscrizioni",
      value: stats.totalBookings,
      icon: Calendar,
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      title: "Utenti Registrati",
      value: stats.totalUsers,
      icon: Users,
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
    {
      title: "Corsi Prossimi",
      value: stats.upcomingCourses,
      icon: TrendingUp,
      color: "text-amber-600",
      bg: "bg-amber-100",
    },
  ];

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Dashboard Admin
            </h1>
            <p className="text-muted-foreground">
              Gestisci corsi, iscrizioni e utenti
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
            <Link to="/admin/mappa">
              <Button className="rounded-full bg-gradient-to-r from-[#5A3A3A] to-[#7A4856] text-white hover:opacity-90 shadow-md" data-testid="admin-mappa-btn">
                <Layers className="w-4 h-4 mr-2" />
                🗺️ Mappa Funzionalità
              </Button>
            </Link>
            <Button
              variant="outline"
              className="rounded-full border-green-300 text-green-700 hover:bg-green-50"
              onClick={triggerSync}
              disabled={syncing}
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync LIVE
            </Button>
            <Link to="/admin/mammouth-ai">
              <Button variant="outline" className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50">
                <Brain className="w-4 h-4 mr-2" />
                Mammouth AI
              </Button>
            </Link>
            <Link to="/admin/hotel-spa">
              <Button variant="outline" className="rounded-full border-teal-300 text-teal-700 hover:bg-teal-50">
                <Hotel className="w-4 h-4 mr-2" />
                Hotel & SPA
              </Button>
            </Link>
            <Link to="/admin/bacheca">
              <Button variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="admin-bacheca-btn">
                <Megaphone className="w-4 h-4 mr-2" />
                Bacheca ODR
              </Button>
            </Link>
            <Link to="/admin/crm">
              <Button variant="outline" className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="admin-crm-btn">
                <Briefcase className="w-4 h-4 mr-2" />
                CRM ITA318
              </Button>
            </Link>
            <Link to="/admin/corsi">
              <Button variant="outline" className="rounded-full border-purple-400 text-purple-700 hover:bg-purple-50" data-testid="admin-videocorsi-btn">
                <PlayCircle className="w-4 h-4 mr-2" />
                Gestione Videocorsi
              </Button>
            </Link>
            <a href="https://web.whatsapp.com/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-green-400 text-green-700 hover:bg-green-50" data-testid="admin-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Web
              </Button>
            </a>
            <Link to="/admin/crm/clienti">
              <Button variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="admin-crm-clienti-btn">
                <Users className="w-4 h-4 mr-2" />
                Clienti ITA318
              </Button>
            </Link>
            <Link to="/admin/crm/ordini">
              <Button variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="admin-crm-ordini-btn">
                <Inbox className="w-4 h-4 mr-2" />
                Ordini CRM
              </Button>
            </Link>
            <Link to="/longevity-academy" target="_blank">
              <Button variant="outline" className="rounded-full border-2 border-[#B08D57] text-[#1C2B24] hover:bg-[#FAF8F3] font-semibold" data-testid="admin-longevity-btn">
                <Leaf className="w-4 h-4 mr-2" style={{ color: "#B08D57" }} />
                Longevity Academy
              </Button>
            </Link>
            <Link to="/academy-benessere-integrato" target="_blank">
              <Button variant="outline" className="rounded-full border-2 border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-semibold" data-testid="admin-academy-benessere-btn">
                <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                Academy Benessere
              </Button>
            </Link>
            <Link to="/admin/leadfinder">
              <Button variant="outline" className="rounded-full border-2 border-blue-400 text-blue-800 hover:bg-blue-50 font-semibold" data-testid="admin-leadfinder-btn">
                <Inbox className="w-4 h-4 mr-2 text-blue-500" />
                Lead Finder Italia
              </Button>
            </Link>
            <Link to="/botanical-oxygen-care" target="_blank">
              <Button variant="outline" className="rounded-full border-2 border-[#C9A961] text-[#3E4E44] hover:bg-[#FBFAF6] font-semibold" data-testid="admin-botanical-btn">
                <Leaf className="w-4 h-4 mr-2" style={{ color: "#C9A961" }} />
                Botanical Oxygen Care
              </Button>
            </Link>
            <Link to="/admin/iscrizioni">
              <Button className="rounded-full">
                Vedi Iscrizioni
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat) => (
                <Card key={stat.title} className="rounded-2xl border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                        <p className="text-3xl font-semibold">{stat.value}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-full ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Bookings */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl">Ultime Iscrizioni</CardTitle>
                <Link to="/admin/iscrizioni">
                  <Button variant="ghost" className="rounded-full text-sm">
                    Vedi tutte <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {recentBookings.length > 0 ? (
                  <div className="space-y-4">
                    {recentBookings.map((booking) => (
                      <div
                        key={booking.booking_id}
                        className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium">{booking.user_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {booking.course_title}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{booking.centro_estetico}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(booking.created_at).toLocaleDateString("it-IT")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Nessuna iscrizione recente
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Wellness & Newsletter Stats Section */}
            {wellnessStats && (
              <div className="mt-8">
                <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-pink-500" />
                  Statistiche Wellness & Newsletter
                </h2>
                
                {/* Wellness Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-pink-50 to-rose-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Iscritti Newsletter</p>
                          <p className="text-3xl font-semibold text-pink-600">{wellnessStats.newsletter?.total_subscribers || 0}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            +{wellnessStats.newsletter?.new_last_30_days || 0} ultimi 30gg
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                          <Mail className="w-6 h-6 text-pink-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-violet-50 to-purple-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Articoli Pubblicati</p>
                          <p className="text-3xl font-semibold text-violet-600">{wellnessStats.articles?.published || 0}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {wellnessStats.articles?.draft || 0} in bozza
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-violet-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-cyan-50 to-teal-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Domande AI Ricevute</p>
                          <p className="text-3xl font-semibold text-cyan-600">{wellnessStats.questions?.total || 0}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {wellnessStats.questions?.completed || 0} risposte generate
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-cyan-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Articoli da Domande</p>
                          <p className="text-3xl font-semibold text-amber-600">{wellnessStats.articles?.ai_generated_from_questions || 0}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            generati con AI
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <Brain className="w-6 h-6 text-amber-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Topics Chart */}
                  {wellnessStats.questions?.top_topics && Object.keys(wellnessStats.questions.top_topics).length > 0 && (
                    <Card className="rounded-2xl border-border/50">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-purple-500" />
                          Argomenti Più Richiesti
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={Object.entries(wellnessStats.questions.top_topics).map(([name, value]) => ({ name, value }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Subscribers by Sector */}
                  {wellnessStats.newsletter?.by_sector && (
                    <Card className="rounded-2xl border-border/50">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-pink-500" />
                          Iscritti per Settore
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-center">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: "Estetico", value: wellnessStats.newsletter.by_sector.estetico || 0 },
                                  { name: "Altro", value: wellnessStats.newsletter.by_sector.altro || 0 }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                                label={({ name, value }) => `${name}: ${value}`}
                              >
                                {[0, 1].map((_, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-violet-500" />
                            <span className="text-sm">Estetico</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-pink-500" />
                            <span className="text-sm">Altro</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Articles by Category */}
                  {wellnessStats.articles?.by_sector && Object.keys(wellnessStats.articles.by_sector).length > 0 && (
                    <Card className="rounded-2xl border-border/50 lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-teal-500" />
                          Articoli per Categoria
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-3">
                          {Object.entries(wellnessStats.articles.by_sector).map(([sector, count], i) => (
                            <Badge 
                              key={sector} 
                              className="px-4 py-2 text-sm"
                              style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + '20', color: CHART_COLORS[i % CHART_COLORS.length] }}
                            >
                              {sector}: {count}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* === AI STATS: Domande Utenti & Articoli Generati === */}
            {aiStats && (
              <div className="mt-8">
                <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center gap-2">
                  <Brain className="w-6 h-6 text-violet-500" />
                  Domande AI & Articoli Generati
                </h2>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="rounded-xl border-cyan-200/50 bg-cyan-50/30" data-testid="ai-stat-domande">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-cyan-700">{aiStats.domande.totale}</p>
                      <p className="text-xs text-muted-foreground mt-1">Domande Ricevute</p>
                      <p className="text-[10px] text-cyan-600">{aiStats.domande.completate} completate &middot; {aiStats.domande.in_attesa} in attesa</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-violet-200/50 bg-violet-50/30" data-testid="ai-stat-articoli">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-violet-700">{aiStats.articoli.da_domande_utenti}</p>
                      <p className="text-xs text-muted-foreground mt-1">Articoli da Domande</p>
                      <p className="text-[10px] text-violet-600">di {aiStats.articoli.totale} totali</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-green-200/50 bg-green-50/30" data-testid="ai-stat-auto">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-green-700">{aiStats.articoli.generati_automaticamente}</p>
                      <p className="text-xs text-muted-foreground mt-1">Generati Auto</p>
                      <p className="text-[10px] text-green-600">{aiStats.articoli.pubblicati} pubblicati</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-pink-200/50 bg-pink-50/30" data-testid="ai-stat-newsletter">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-pink-700">{aiStats.newsletter.attivi}</p>
                      <p className="text-xs text-muted-foreground mt-1">Iscritti Attivi</p>
                      <p className="text-[10px] text-pink-600">di {aiStats.newsletter.totale_iscritti} totali</p>
                    </CardContent>
                  </Card>
                </div>

                {/* User Questions Articles Table */}
                {aiStats.articoli.articoli_utenti && aiStats.articoli.articoli_utenti.length > 0 && (
                  <Card className="rounded-xl border-border/50" data-testid="ai-user-articles-table">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-cyan-500" />
                        Articoli Generati da Domande Utenti ({aiStats.articoli.articoli_utenti.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="p-2 text-left font-medium text-xs">Data</th>
                              <th className="p-2 text-left font-medium text-xs">Email Utente</th>
                              <th className="p-2 text-left font-medium text-xs">Nome</th>
                              <th className="p-2 text-left font-medium text-xs">Domanda / Titolo Articolo</th>
                              <th className="p-2 text-left font-medium text-xs">Stato</th>
                              <th className="p-2 text-center font-medium text-xs">Link</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aiStats.articoli.articoli_utenti.map((a, i) => (
                              <tr key={a.article_id || i} className="border-b hover:bg-gray-50/50">
                                <td className="p-2 text-xs whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString("it-IT") : "-"}</td>
                                <td className="p-2 text-xs">
                                  {a.user_email ? <a href={`mailto:${a.user_email}`} className="text-violet-600 hover:underline">{a.user_email}</a> : "-"}
                                </td>
                                <td className="p-2 text-xs">{a.user_name || "-"}</td>
                                <td className="p-2 text-xs">
                                  <p className="font-medium max-w-[300px] truncate">{a.title || "-"}</p>
                                  {a.original_question && <p className="text-muted-foreground text-[11px] max-w-[300px] truncate mt-0.5">{a.original_question}</p>}
                                </td>
                                <td className="p-2">
                                  <Badge variant="outline" className={`text-[10px] ${a.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                    {a.status || "?"}
                                  </Badge>
                                </td>
                                <td className="p-2 text-center">
                                  {a.article_id && (
                                    <a href={`/benessere/articolo/${a.article_id}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-800">
                                      <ArrowRight className="w-4 h-4 inline" />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Questions (not yet processed) */}
                {aiStats.domande.lista && aiStats.domande.lista.length > 0 && (
                  <Card className="rounded-xl border-border/50 mt-4" data-testid="ai-questions-list">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Ultime Domande Ricevute ({aiStats.domande.lista.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="p-2 text-left font-medium text-xs">Data</th>
                              <th className="p-2 text-left font-medium text-xs">Email</th>
                              <th className="p-2 text-left font-medium text-xs">Domanda</th>
                              <th className="p-2 text-left font-medium text-xs">Stato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aiStats.domande.lista.map((q, i) => (
                              <tr key={q.question_id || i} className="border-b hover:bg-gray-50/50">
                                <td className="p-2 text-xs whitespace-nowrap">{q.created_at ? new Date(q.created_at).toLocaleDateString("it-IT") : "-"}</td>
                                <td className="p-2 text-xs">{q.email || q.user_email || "-"}</td>
                                <td className="p-2 text-xs max-w-[350px] truncate">{q.domanda || q.question || "-"}</td>
                                <td className="p-2">
                                  <Badge variant="outline" className={`text-[10px] ${q.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                    {q.status || "?"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminDashboard;
